import './SystemHealth.scss';
import { useEffect, useState } from 'react';
import { CopyButton } from '@Components/CopyButton';
import cn from 'classnames';
import { MdCheck } from 'react-icons/md';
import { Button } from '../../Components/Button';
import { Appearance, Design, Size } from '../../Components/designConstants';
import { Page } from '../../Components/Page';
import { Spinner } from '../../Components/Spinner';
import { useAppSelector } from '../../redux';
import { useSetError } from '../../redux/modals';
import * as UserRedux from '../../redux/user';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { useApplySystemHealthFix } from './hooks/useApplySystemHealthFix';
import { useSystemHealthData } from './hooks/useSystemHealthData';
import {
  HealthCheckResult,
  HealthCheckRow,
  HealthStatusType,
  STATUS_LABELS
} from './systemHealthConstants';
import {
  formatCellValue,
  formatLastRunAt,
  getRowFix,
  getRowFixKey,
  groupByCategory,
  rowsToTsv,
  RowFix
} from './systemHealthUtils';

const PAGE_NAME = 'System Health';

// Sentinel `pendingTarget` value for the "Run all" bulk action, distinct from any real fix key
// (which is always prefixed with "task:" or "commission:" -- see getRowFixKey).
const RUN_ALL_TARGET = '__run_all__';

export const SystemHealth = () => {
  const { data, isLoading, error } = useSystemHealthData();
  const setError = useSetError();

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load system health",
        message: `Failed to load system health checks: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  return (
    <Page title='System Health' pageType='System Health' isLoading={isLoading}>
      <SystemHealthBody data={data} isLoading={isLoading} />
    </Page>
  );
};

interface SystemHealthBodyProps {
  data?: HealthCheckResult[];
  isLoading: boolean;
}

const SystemHealthBody = ({ data, isLoading }: SystemHealthBodyProps) => {
  if (isLoading) {
    return <Spinner />;
  }
  if (!data || data.length === 0) {
    return <div className='system-health__empty'>No checks configured.</div>;
  }

  const categories = groupByCategory(data);

  return (
    <div className='system-health'>
      {categories.map(({ category, checks }) => (
        <CategorySection key={category} category={category} checks={checks} />
      ))}
    </div>
  );
};

interface CategorySectionProps {
  category: string;
  checks: HealthCheckResult[];
}

const CategorySection = ({ category, checks }: CategorySectionProps) => (
  <div className='system-health__category'>
    <h2 className='system-health__category-title'>{category}</h2>
    <div className='system-health__cards'>
      {checks.map(check => (
        <CheckCard key={check.key} check={check} />
      ))}
    </div>
  </div>
);

interface CheckCardProps {
  check: HealthCheckResult;
}

const CheckCard = ({ check }: CheckCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = check.rows.length > 0;

  return (
    <div className='system-health-card'>
      <div className='system-health-card__header'>
        <span className='system-health-card__title'>{check.title}</span>
        <StatusPill status={check.status} />
      </div>
      <p className='system-health-card__guidance'>{check.guidance}</p>
      <div className='system-health-card__meta'>
        <span>
          {check.rowCount} row{check.rowCount === 1 ? '' : 's'}
        </span>
        <span>{formatLastRunAt(check.lastRunAt)}</span>
        {hasDetails && (
          <Button
            size={Size.SM}
            appearance={Appearance.EMPTY}
            design={Design.NEUTRAL}
            onClick={() => setExpanded(prev => !prev)}
          >
            {expanded ? 'Hide details' : 'View details'}
          </Button>
        )}
      </div>
      {expanded && hasDetails && <DetailTable checkKey={check.key} rows={check.rows} />}
    </div>
  );
};

interface StatusPillProps {
  status: HealthStatusType;
}

const StatusPill = ({ status }: StatusPillProps) => (
  <span className={cn('system-health-pill', `system-health-pill--${status}`)}>
    {STATUS_LABELS[status]}
  </span>
);

interface DetailTableProps {
  checkKey: string;
  rows: HealthCheckRow[];
}

const DetailTable = ({ checkKey, rows }: DetailTableProps) => {
  const columns = Object.keys(rows[0] ?? {});
  const [appliedFixKeys, setAppliedFixKeys] = useState<Set<string>>(new Set());
  const [pendingTarget, setPendingTarget] = useState<string | null>(null);
  const { mutateAsync: applyFix, isPending } = useApplySystemHealthFix();
  // The backend only lets admins (tinuitiAdminOnlyWrapper) actually run a fix -- hide the
  // controls for everyone else rather than showing a button that just 403s.
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);

  const hasFixColumn = isAdmin && rows.some(row => getRowFix(row) !== null);
  const fixableFixes = isAdmin
    ? Array.from(
        new Map(
          rows
            .map(getRowFix)
            .filter((fix): fix is RowFix => fix !== null)
            .filter(fix => !appliedFixKeys.has(getRowFixKey(fix)))
            .map(fix => [getRowFixKey(fix), fix] as const)
        ).values()
      )
    : [];

  const applyFixes = async (fixes: RowFix[], pendingTargetValue: string) => {
    setPendingTarget(pendingTargetValue);
    try {
      const tasks = fixes
        .filter((fix): fix is Extract<RowFix, { type: 'task' }> => fix.type === 'task')
        .map(({ taskName, enabled }) => ({ taskName, enabled }));
      const commission = fixes
        .filter((fix): fix is Extract<RowFix, { type: 'commission' }> => fix.type === 'commission')
        .map(({ company, month, commission: value }) => ({ company, month, commission: value }));

      await applyFix({ key: checkKey, tasks, commission });
      setAppliedFixKeys(prev => new Set([...prev, ...fixes.map(getRowFixKey)]));
      Mixpanel.track(MxE.ActionTriggered, { page: PAGE_NAME, action: `apply_${checkKey}` });
    } catch {
      // Already surfaced to the user via the mutation's own error modal.
    } finally {
      setPendingTarget(null);
    }
  };

  return (
    <div className='system-health-detail'>
      <div className='system-health-detail__toolbar'>
        {fixableFixes.length > 0 && (
          <Button
            size={Size.SM}
            appearance={Appearance.OUTLINED}
            design={Design.BRAND}
            loading={pendingTarget === RUN_ALL_TARGET}
            disabled={isPending && pendingTarget !== RUN_ALL_TARGET}
            onClick={() => applyFixes(fixableFixes, RUN_ALL_TARGET)}
          >
            {`Run all ${fixableFixes.length}`}
          </Button>
        )}
        <CopyButton label='Copy all rows' text={rowsToTsv(rows)} />
      </div>
      <div className='system-health-detail-table'>
        <table>
          <thead>
            <tr>
              {columns.map(column => (
                <th key={column}>{column}</th>
              ))}
              {hasFixColumn && <th>Fix</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const fix = getRowFix(row);
              const fixKey = fix && getRowFixKey(fix);
              return (
                <tr key={index}>
                  {columns.map(column => (
                    <td key={column}>{formatCellValue(row[column])}</td>
                  ))}
                  {hasFixColumn && (
                    <td>
                      <FixCell
                        fix={fix}
                        applied={fixKey !== null && appliedFixKeys.has(fixKey)}
                        pending={pendingTarget === fixKey}
                        disabled={isPending && pendingTarget !== fixKey}
                        onApply={rowFix => applyFixes([rowFix], getRowFixKey(rowFix))}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface FixCellProps {
  fix: RowFix | null;
  applied: boolean;
  pending: boolean;
  disabled: boolean;
  onApply: (fix: RowFix) => void;
}

const FixCell = ({ fix, applied, pending, disabled, onApply }: FixCellProps) => {
  if (!fix) return null;

  if (applied) {
    return (
      <span className='system-health-detail__applied'>
        <MdCheck /> Applied
      </span>
    );
  }

  return (
    <Button
      size={Size.SM}
      appearance={Appearance.OUTLINED}
      design={Design.BRAND}
      loading={pending}
      disabled={disabled}
      onClick={() => onApply(fix)}
    >
      Apply
    </Button>
  );
};
